import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { BackToList, DangerAction, Group, Row, type Status } from '../settings/SettingsList'
import { ConfirmDialog } from '../settings/ConfirmDialog'
import { useAuth } from '../auth/AuthProvider'
import type { IconName } from '../settings/Icon'
import { ProfileSection } from '../settings/ProfileSection'
import { ZohoSection } from '../settings/ZohoSection'
import { DraftModelSection } from '../settings/DraftModelSection'
import { KnowledgeSection } from '../settings/KnowledgeSection'
import { InstructionsSection } from '../settings/InstructionsSection'
import { TonesSection } from '../settings/TonesSection'
import { SendFromSection } from '../settings/SendFromSection'
import { AutomationSection } from '../settings/AutomationSection'
import { deleteMyAccount, resetAccount, resetOrg } from '../api/settings'
import { getOrgSettings, type OrgSettings } from '../api/orgSettings'
import { listDocuments } from '../api/knowledge'
import type { MeResponse, MissingCredential } from '../types'
import { useDeskOrg } from '../hooks/useDeskOrg'
import { useZohoCallbackResult } from '../hooks/useZohoCallbackResult'
import { FeedbackSection } from '../settings/FeedbackSection'

/**
 * What the list can say about the three settings whose state lives inside their
 * own section. Two small reads, made once, so a row can say "2 documents"
 * without the agent opening it to find out.
 */
function useOrgFacts(ready: boolean) {
  const [docs, setDocs] = useState<number | null>(null)
  const [style, setStyle] = useState<boolean | null>(null)
  const [auto, setAuto] = useState<string | null>(null)
  const [tones, setTones] = useState<number | null>(null)
  const [from, setFrom] = useState<string | null>(null)
  const [canSend, setCanSend] = useState<boolean | null>(null)
  const [desk, setDesk] = useState<OrgSettings['deskUser'] | null>(null)

  const load = useCallback(async () => {
    if (!ready) return
    try {
      const [documents, settings] = await Promise.all([listDocuments(), getOrgSettings()])
      setDocs(documents.documents.length)
      setStyle(Boolean(settings.customInstructions))
      setAuto(settings.automation.mode)
      setTones(settings.tones?.length ?? 0)
      setFrom(settings.automation.fromEmailAddress || '')
      setCanSend(settings.canSendEmail ?? null)
      setDesk(settings.deskUser ?? null)
    } catch {
      // A list that cannot say the count is still a usable list.
    }
  }, [ready])

  useEffect(() => {
    void load()
  }, [load])

  return { docs, style, auto, tones, from, canSend, desk, isAdmin: Boolean(desk?.admin), reload: load }
}

/** Each automation mode, said as what it does rather than what it is called. */
const AUTO_DETAIL: Record<string, string> = {
  off: 'Nothing happens automatically',
  draft: 'Posts a private comment for agents',
  send: 'Emails the customer, unreviewed',
}

export function SettingsView({
  me,
  missing,
  compact,
  refresh,
  onBack,
}: {
  me: MeResponse | null
  missing: MissingCredential[]
  compact?: boolean
  refresh: () => void
  onBack?: () => void
}) {
  const { signOut } = useAuth()
  const [open, setOpen] = useState<string | null>(null)

  const connected = Boolean(me?.zoho.valid)

  // Either the callback just said so, or the help desk the widget is open in
  // is not the one the connection is for. Same problem, and the list has to
  // show it: the callback lands here, not on the Zoho screen.
  const deskOrg = useDeskOrg()
  const callbackSaid = useZohoCallbackResult()
  const connectedOrg = me?.zoho.orgId ? String(me.zoho.orgId) : ''
  const wrongPortal =
    callbackSaid === 'wrong_portal' ||
    Boolean(connected && deskOrg && connectedOrg && deskOrg !== connectedOrg)
  const facts = useOrgFacts(connected)

  /**
   * Leaving a screen re-reads what the list shows.
   *
   * These facts were read once when Settings opened, so saving a new
   * auto-reply mode and coming back left the row describing the old one — the
   * list quietly disagreeing with the screen the agent had just used.
   */
  const closeScreen = () => {
    setOpen(null)
    void facts.reload()
  }

  // A dialog rather than a second press on the same row. Press-twice changed
  // the label under the pointer, so the confirming press was aimed at a row
  // that no longer said what it had said — and a double-tap did the whole
  // thing. Erasing the team's material additionally asks for the word to be
  // typed: that one costs somebody else their afternoon.
  const [asking, setAsking] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [done, setDone] = useState('')
  const [failed, setFailed] = useState('')

  function danger(id: string, run: () => Promise<string>) {
    return async () => {
      setBusy(id)
      setFailed('')
      try {
        setDone(await run())
        await facts.reload()
        refresh()
        // Back to the list. Staying on the danger screen left the buttons
        // sitting under a line saying the deed was done, inviting a second
        // press at something there was no longer any reason to press.
        closeScreen()
      } catch (e) {
        // Without this the rejection vanished: the call site is `void danger()`,
        // so a failed reset left the row looking untouched and said nothing.
        setFailed(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(null)
        setAsking(null)
      }
    }
  }

  const forgetMine = danger('mine', async () => {
    await resetAccount()
    return 'Your connection and keys have been removed.'
  })

  const eraseOrg = danger('org', async () => {
    const r = await resetOrg()
    // Counted in the things the team added — files and pages — not in the
    // chunks we split them into. "Erased 30 sections" for one PDF read like
    // the app had deleted more than anyone had ever put in.
    const files = Math.max(r.sources - r.pages, 0)
    const bits = [
      files ? `${files} document${files === 1 ? '' : 's'}` : '',
      r.pages ? `${r.pages} page${r.pages === 1 ? '' : 's'}` : '',
    ].filter(Boolean)
    return bits.length
      ? `Erased ${bits.join(' and ')}, and cleared the reply style.`
      : 'Cleared the reply style. There were no documents or pages.'
  })

  const closeAccount = danger('gone', async () => {
    await deleteMyAccount()
    // Nothing to return to: the account this panel was signed into no longer
    // exists, so the gate is the only honest next screen.
    await signOut()
    return 'Your account has been deleted.'
  })

  const provider = me?.draftProvider
  const key = provider ? me?.providers?.[provider] : undefined
  const modelLabel = me?.providerCatalogue?.find((p) => p.id === provider)?.label ?? provider

  const ok: Status = { text: '', tone: 'ok' }

  const screens: {
    id: string
    icon: IconName
    avatar?: string | null
    danger?: boolean
    name: string
    detail: string
    status: Status
    group: string
    shown: boolean
    node: ReactNode
  }[] = [
    {
      id: 'zoho',
      icon: 'link',
      name: 'Zoho account',
      detail: wrongPortal
        ? 'Wrong help desk. Open to fix'
        : connected
          ? // The desk's own name first: it is the thing that can be wrong,
            // and the data centre alone never told anyone which desk they
            // were on.
            `${me?.zoho.orgName || me?.zoho.orgId || 'Connected'} · zoho.${me?.zoho.dc}`
          : 'Reads tickets, posts replies',
      // A green tick over a connection that cannot read this portal's tickets
      // is the whole bug the agent reported: the callback said wrong portal
      // and the list still looked finished.
      status: wrongPortal
        ? { text: 'Wrong desk', tone: 'warn' }
        : connected
          ? ok
          : { text: 'Connect', tone: 'warn' },
      group: 'Connection',
      shown: true,
      node: <ZohoSection me={me} desk={facts.desk} compact={compact} onChanged={refresh} />,
    },
    {
      id: 'model',
      icon: 'spark',
      name: 'Model',
      detail: key?.valid ? `${modelLabel} · key ending ${key.hint}` : 'Generates the replies',
      status: key?.valid ? ok : { text: 'Add key', tone: 'warn' },
      group: 'Connection',
      shown: true,
      node: <DraftModelSection me={me} onChanged={refresh} />,
    },
    {
      id: 'docs',
      icon: 'docs',
      name: 'Documents',
      detail: 'Files and pages it answers from',
      status:
        facts.docs === null
          ? null
          : facts.docs === 0
            ? { text: 'None', tone: 'warn' }
            : { text: String(facts.docs), tone: 'plain' },
      group: 'What it writes from',
      shown: connected,
      node: <KnowledgeSection me={me} isAdmin={facts.isAdmin} />,
    },
    {
      id: 'tones',
      icon: 'spark',
      name: 'Tones',
      detail: 'Buttons your team taps at a ticket',
      status:
        facts.tones === null ? null : facts.tones ? { text: `${facts.tones}`, tone: 'plain' } : { text: 'None', tone: 'plain' },
      group: 'How it writes',
      shown: connected,
      node: <TonesSection />,
    },
    {
      id: 'sendfrom',
      icon: 'link',
      name: 'Sending address',
      // The one row that can say a reply will not leave the building.
      detail: facts.canSend === false ? 'No address in Zoho Desk yet' : 'Where emailed replies come from',
      status:
        facts.canSend === false
          ? { text: 'Set up', tone: 'warn' }
          : facts.from
            ? { text: 'Fallback set', tone: 'plain' }
            : null,
      group: 'How it writes',
      shown: connected,
      node: <SendFromSection />,
    },
    {
      id: 'style',
      icon: 'chat',
      name: 'Reply instructions',
      detail: 'Your tone and wording',
      status: facts.style === null ? null : facts.style ? ok : { text: 'Default', tone: 'plain' },
      group: 'How it writes',
      shown: connected,
      node: <InstructionsSection />,
    },
    {
      id: 'profile',
      icon: 'user',
      avatar: me?.photoURL,
      name: 'Profile',
      detail: me?.email ?? 'Your name and picture',
      status: null,
      group: 'You',
      shown: true,
      node: <ProfileSection me={me} compact={compact} onChanged={refresh} />,
    },
    {
      id: 'auto',
      icon: 'bolt',
      name: 'Auto-reply',
      // What it is set to right now, not what the feature is for. A row that
      // reads the same whether replies are being emailed to customers or
      // nothing is happening at all is telling the agent nothing.
      detail: AUTO_DETAIL[facts.auto ?? ''] ?? 'Answers new messages on its own',
      // Off is not a finished state — it is the setting an agent most expects
      // to be on, and nothing on the ticket screen reveals that it is not.
      status: facts.auto === null ? null : facts.auto === 'off' ? { text: 'Off', tone: 'warn' } : ok,
      group: 'Without an agent',
      shown: connected,
      node: <AutomationSection compact={compact} />,
    },
  ]

  screens.push({
    id: 'feedback',
    icon: 'chat',
    name: 'Send feedback',
    detail: 'Tell us what is working and what is not',
    status: null,
    // Its own group at the end. Second from the top it read as a setup step,
    // and the first thing an agent needs is the connection, not us.
    group: 'Anything else',
    shown: true,
    node: <FeedbackSection />,
  });

  screens.push({
    id: 'danger',
    icon: 'unlink',
    name: 'Reset or erase',
    detail: 'Undo your setup, or clear what the team has taught the app',
    status: null,
    danger: true,
    group: 'Danger zone',
    // Shown to anyone signed in, not only to a connected account. Leaving is
    // the one thing that must not depend on setup having worked: someone who
    // signed up, never connected Zoho and wants their data gone could not
    // reach this screen at all, and had to email us to be deleted.
    shown: true,
    node: (
      <div className="card">
        <h2 className="danger-name">Danger zone</h2>

        <div className="group-rows danger-rows">
          {connected && (
            <DangerAction
              icon="unlink"
              name="Reset my setup"
              detail="Your connection and keys. Team documents are untouched."
              busy={busy === 'mine'}
              onAct={() => {
                setDone('')
                setAsking('mine')
              }}
            />
          )}

          {/* Both of these reach into Zoho — one to find the org, the other
              to know there is a connection to drop. Without one there is
              nothing for them to do, and leaving is still possible below. */}
          {/* Only to whoever runs the help desk. This takes the documents and
              settings away from colleagues who are not in the room, and Zoho
              already knows who is entitled to do that. The server refuses it
              either way; this keeps the button out of everyone else's reach
              rather than offering one that answers 403. */}
          {connected && facts.isAdmin && (
            <DangerAction
              icon="docs"
              name="Erase everything"
              detail="The whole team loses the documents and settings"
              busy={busy === 'org'}
              onAct={() => {
                setDone('')
                setAsking('org')
              }}
            />
          )}

          <DangerAction
            icon="unlink"
            name="Delete my account"
            detail="Everything of yours, including the way you sign in"
            busy={busy === 'gone'}
            onAct={() => {
              setDone('')
              setAsking('gone')
            }}
          />

          {done && <p className="row-note done">{done}</p>}
          {failed && <p className="row-note failed">{failed}</p>}
        </div>

        {asking === 'mine' && (
          <ConfirmDialog
            title="Reset your setup?"
            body="This removes your Zoho connection and every API key you saved. The team's documents, reply style and tones are untouched. You can set it up again in a couple of minutes."
            confirmLabel="Reset my setup"
            busy={busy === 'mine'}
            onCancel={() => setAsking(null)}
            onConfirm={() => void forgetMine()}
          />
        )}

        {asking === 'gone' && (
          <ConfirmDialog
            title="Delete your account?"
            body="Your Zoho connection, API keys, profile and anything you uploaded for yourself are deleted, along with the account you sign in with. Documents you shared with the team stay with the team. This cannot be undone."
            confirmLabel="Delete my account"
            confirmWord="DELETE"
            busy={busy === 'gone'}
            onCancel={() => setAsking(null)}
            onConfirm={() => void closeAccount()}
          />
        )}

        {asking === 'org' && (
          <ConfirmDialog
            title="Erase everything?"
            body="Every document and linked page goes, the reply style and tones are cleared, and auto-reply is switched off for everyone in your Zoho organisation. This cannot be undone."
            confirmLabel="Erase everything"
            confirmWord="ERASE"
            busy={busy === 'org'}
            onCancel={() => setAsking(null)}
            onConfirm={() => void eraseOrg()}
          />
        )}
      </div>
    ),
  })

  const showing = screens.find((s) => s.id === open)

  // The section keeps its own heading, so the screen adds only the way back.
  if (showing) {
    return (
      <>
        <BackToList onBack={closeScreen} />
        {showing.node}
      </>
    )
  }

  // Whose account this is comes first: it is the thing an agent checks at a
  // glance, and the rest of the list is setup they touch once.
  const groups = ['You', 'Connection', 'What it writes from', 'How it writes', 'Without an agent', 'Anything else', 'Danger zone']
    .map((name) => ({ name, rows: screens.filter((s) => s.group === name && s.shown) }))
    .filter((g) => g.rows.length > 0)

  return (
    <>
      {/* Above the title, like the back link on every screen inside settings:
          the way out sits at the top left, not underneath the heading it is
          leaving. Shown whether or not setup is finished — hiding it until
          everything was connected trapped an agent here the moment anything
          broke, and a rejected API key was enough. */}
      {onBack && <BackToList label="Back to ticket" onBack={onBack} />}

      <h1>Settings</h1>

      {missing.length > 0 && <p className="sub">Finish setup to start generating replies.</p>}

      {groups.map((g) => (
        <Group key={g.name} name={g.name === 'You' ? '' : g.name}>
          {g.rows.map((s) => (
            <Row
              key={s.id}
              icon={s.icon}
              avatar={s.avatar}
              name={s.name}
              detail={s.detail}
              status={s.status}
              danger={s.danger}
              onOpen={() => setOpen(s.id)}
            />
          ))}
        </Group>
      ))}

      {/* The guide, for the moment a screen here does not explain itself.
          A new tab: the panel is an iframe inside Desk, and navigating it
          away would take the app with it. */}
      <p className="settings-help">
        <a href="https://orovim.com/help" target="_blank" rel="noreferrer">
          Help &amp; setup guide
        </a>
      </p>
    </>
  )
}
